import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import appsService from '../services/apps';
import { getToken, getIsPortalAdmin } from '../utils/storage';

const AppsContext = createContext(null);

export const useApps = () => {
    const context = useContext(AppsContext);
    if (!context) {
        throw new Error('useApps must be used within AppsProvider');
    }
    return context;
};

export const AppsProvider = ({ children }) => {
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const hasLoadedRef = useRef(false);
    const isLoadingRef = useRef(false);
    const initialLoadAttemptedRef = useRef(false);

    const loadApps = useCallback(async (forceRefresh = false) => {
        const token = getToken();
        if (!token) {
            hasLoadedRef.current = false;
            setApps([]);
            setLoading(false);
            return;
        }

        if (!forceRefresh && hasLoadedRef.current) return;
        if (isLoadingRef.current) return; // Prevent concurrent calls

        isLoadingRef.current = true;
        setLoading(true);
        setError('');
        try {
            // Portal admins can list every app; app-scoped users only their own.
            const data = getIsPortalAdmin() ? await appsService.getAll() : await appsService.getMine();
            setApps(data || []);
            hasLoadedRef.current = true;
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to load apps');
            hasLoadedRef.current = false; // Reset on error so it can retry
        } finally {
            setLoading(false);
            isLoadingRef.current = false;
        }
    }, []);

    const refreshApps = useCallback(async () => {
        hasLoadedRef.current = false; // Reset ref to force refresh
        await loadApps(true);
    }, [loadApps]);

    // Expose a method to ensure apps are loaded (useful after login)
    // This will be called from ShellLayout when entering protected routes
    const ensureAppsLoaded = useCallback(() => {
        // Prevent duplicate calls
        if (initialLoadAttemptedRef.current || isLoadingRef.current || hasLoadedRef.current) {
            return;
        }

        initialLoadAttemptedRef.current = true;
        const token = getToken();
        if (token) {
            loadApps();
        } else {
            setLoading(false);
        }
    }, [loadApps]);

    return (
        <AppsContext.Provider value={{ apps, loading, error, refreshApps, ensureAppsLoaded }}>
            {children}
        </AppsContext.Provider>
    );
};
